import { canAccessPath, hasPermission } from '../app/access';
import { designerNavigation, filterNavigation, fullNavigation, type NavNode } from '../app/navigation';
import type { AnyTourName, ModalTourName, TourName, WorkflowTourName } from './types';

export interface TourAccessContext {
  role?: string;
  permissions?: readonly string[];
}

export const TOUR_ROUTES: Record<AnyTourName, readonly string[]> = {
  products: ['/products'],
  wildberries: ['/wildberries'],
  ozon: ['/ozon'],
  designs: ['/designs'],
  categories: ['/categories'],
  dashboard: ['/'],
  sellers: ['/sellers'],
  users: ['/users'],
  orders: ['/orders'],
  settings: ['/settings'],
  'designer-create': ['/designer/create'],
  'designer-stats': ['/designer/stats'],
  'designer-payouts': ['/designer/payouts'],
  'designer-tariffs': ['/designer/tariffs'],
  // Внутренних экранов генератора нет в меню: тур доступен тому, у кого в меню есть сам раздел.
  'layout-jobs': ['/layout-generator'],
  'layout-create': ['/layout-generator'],
  'layout-studio': ['/layout-generator'],
  'layout-job': ['/layout-generator'],
  'layout-duplicate': ['/layout-generator'],
  'layout-help': ['/layout-generator'],
  'product-create': ['/products'],
  'product-edit': ['/products'],
  'design-create': ['/designs'],
  'design-edit': ['/designs'],
  'design-import': ['/designs'],
  'designer-theme-create': ['/designer/create'],
  'designer-theme-new': ['/designer/create'],
  'designer-theme-task': ['/designer/create'],
  'designer-theme-upload': ['/designer/create'],
  'designer-pdf-upload': ['/designer/create'],
  'full-cycle': ['/designs', '/categories', '/products', '/media-mappings', '/wildberries'],
  'designer-cabinet': ['/designer/create', '/designer/stats', '/designer/payouts', '/designer/tariffs'],
};

function pathsOf(nodes: readonly NavNode[]): string[] {
  return nodes.flatMap((node) => node.children ? pathsOf(node.children) : node.path ? [node.path.split('?')[0]] : []);
}

export function navigationForSession(context: TourAccessContext): NavNode[] {
  if (!context.role || !context.permissions) return [];
  const source = context.role === 'designer' ? designerNavigation : fullNavigation;
  // Роль передаётся так же, как в Sidebar: без неё пропадают пункты меню с ограничением по ролям (генератор макетов).
  return filterNavigation(source, (access) => hasPermission(access, context.permissions), context.role);
}

export function isRouteAllowed(path: string, context: TourAccessContext): boolean {
  if (!context.role || !context.permissions || !canAccessPath(path, context.permissions)) return false;
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  return pathsOf(navigationForSession(context)).includes(pathname);
}

export function isTourAllowed(tourName: AnyTourName, context: TourAccessContext): boolean {
  return TOUR_ROUTES[tourName].every((path) => isRouteAllowed(path, context));
}

export function allowedWorkflowTours(context: TourAccessContext): WorkflowTourName[] {
  return (['full-cycle', 'designer-cabinet'] as WorkflowTourName[]).filter((name) => isTourAllowed(name, context));
}

export function isPageTourAllowed(tourName: TourName, context: TourAccessContext): boolean {
  return isTourAllowed(tourName, context);
}

export function isModalTourAllowed(tourName: ModalTourName, context: TourAccessContext): boolean {
  return isTourAllowed(tourName, context);
}
